import { linearColor } from '../model/colors.ts';

/**
 * Time-of-day grade parameters and light colors: the pure math of the renderer.
 * Ported verbatim from the original `render/shaders.ts` (`gradeFor`, `lightGainFor`,
 * uniform packing, `HDR_SCALE`) and `render/draw.ts` (constants, `lightColor`,
 * `thrownColor`, per-time factors computed inline in `drawDemoHdr` / `drawDemoLdr`).
 * The shaders and Skia drawing themselves are per-platform (see docs/spec/03-rendering.md).
 */

/** Everything drawn into the half-float layer is linear light × HDR_SCALE (16× headroom). */
export const HDR_SCALE = 1 / 16;

export interface GradeParams {
  exposure: number;
  skyExposure: number;
  desat: number;
  blue: number;
  dusk: number;
  skyAmount: number;
  grain: number;
  vignette: number;
}

export const smooth = (a: number, b: number, x: number) => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

export const NIGHT_EXPOSURE = 0.045;

/** Maps the time-of-day slider to grade parameters. */
export function gradeFor(time: number): GradeParams {
  const t = Math.max(0, Math.min(1, time));
  // Exposure falls off in log space: ~1/5 at evening, ~1/22 at night.
  const exposure = Math.exp(Math.log(NIGHT_EXPOSURE) * Math.pow(t, 1.1));
  return {
    exposure,
    skyExposure: Math.exp(Math.log(0.09) * Math.pow(t, 1.25)) * 1.6,
    desat: 0.55 * smooth(0.15, 1, t),
    blue: smooth(0.25, 1, t),
    dusk: Math.exp(-Math.pow((t - 0.5) / 0.2, 2)),
    skyAmount: smooth(0.12, 0.45, t),
    grain: smooth(0.5, 1, t),
    vignette: 0.35 * smooth(0.3, 1, t),
  };
}

/**
 * How bright the LEDs appear relative to the night exposure. LEDs emit the same light day
 * and night; the camera exposes for the ambient light, so they look 1/22 as bright at noon.
 */
export function lightGainFor(time: number): number {
  return NIGHT_EXPOSURE / gradeFor(time).exposure;
}

/** Uniforms for the LDR grade (`nightGrade`). */
export function gradeUniforms(width: number, height: number, g: GradeParams): number[] {
  return [width, height, g.exposure, g.skyExposure, g.desat, g.blue, g.dusk, g.skyAmount, g.grain, g.vignette];
}

/** Uniforms for the HDR grade (`gradeHdr`). */
export function gradeHdrUniforms(width: number, height: number, g: GradeParams, hasDepth: boolean): number[] {
  return [width, height, g.exposure, g.skyExposure, g.desat, g.blue, g.dusk, g.skyAmount, hasDepth ? 1 : 0, HDR_SCALE];
}

/* --------------------------------------------------------------- renderer constants */

/**
 * Light output of one LED on the wall, relative to a white wall at the night exposure.
 * Neighboring scallops overlap ~2.5×, so the summed peak on a white wall stays under ~0.9.
 */
export const WASH_INTENSITY = 0.45;
/** Each LED of a pair throws less: a pair replaces one single, not two. */
export const PAIR_WASH_FACTOR = 0.6;
/** Camera response to an LED seen head-on at the night exposure (cores clip to white). */
export const CORE_INTENSITY = 60;
export const HALO_INTENSITY = 2.0;
export const BLOOM_INTENSITY = 0.13;
/** Wide bloom radius in core radii. */
export const BLOOM_RADII = 4.5;
/**
 * LED distance from the wall plane, as a fraction of the wash length. Smaller than the LED
 * pitch, so individual scallops show near the eave and blend further down the wall.
 */
export const WALL_OFFSET = 0.12;
/** Depth tolerance (inverse-depth units) for light to land on a surface near the run. */
export const DEPTH_SIGMA = 0.12;
/** Exposure of the wall wash relative to the 8-bit light map (LDR fallback). */
export const WASH_GAIN = 2.2;

/**
 * Per-time factors the demo renderer derives from `scene.time` and `style.brightness`
 * (computed inline in the original `drawDemoHdr` / `drawDemoLdr`).
 */
export interface DemoFactors {
  grade: GradeParams;
  /** HDR: light gain = lightGainFor(time) × brightness. */
  gain: number;
  /** HDR: track/housing visibility = 1 − smooth(0, 0.6, time); drawn when > 0.02. */
  hardware: number;
  drawHardware: boolean;
  /** HDR: vignette pass drawn when grade.vignette > 0.01. */
  drawVignette: boolean;
  /** HDR: grain pass (Overlay, display space) drawn when grade.grain > 0.01, amount grade.grain × 0.09. */
  drawGrain: boolean;
  grainAmount: number;
  /** LDR fallback: night = smooth(0, 1, time). */
  ldrNight: number;
  /** LDR fallback: wash visibility = smooth(0.08, 0.85, time); wash drawn when > 0.01. */
  ldrWashVisibility: number;
  /** LDR fallback: hardware = 1 − night; drawn when > 0.05. */
  ldrHardware: number;
}

export function demoFactors(time: number, brightness: number): DemoFactors {
  const grade = gradeFor(time);
  const hardware = 1 - smooth(0, 0.6, time);
  const night = smooth(0, 1, time);
  return {
    grade,
    gain: lightGainFor(time) * brightness,
    hardware,
    drawHardware: hardware > 0.02,
    drawVignette: grade.vignette > 0.01,
    drawGrain: grade.grain > 0.01,
    grainAmount: grade.grain * 0.09,
    ldrNight: night,
    ldrWashVisibility: smooth(0.08, 0.85, time),
    ldrHardware: 1 - night,
  };
}

/* ---------------------------------------------------------------------- light colors */

/** Linear float color for the `solid`/kernel shaders. */
export function lin(hex: string, k: number): [number, number, number] {
  const [r, g, b] = linearColor(hex);
  return [r * k, g * k, b * k];
}

/**
 * Light color as the camera records it. Night shots are white-balanced warm (≈4000K), so
 * 2700K reads golden rather than orange and 5000K reads near-neutral. Normalized so the
 * brightest channel keeps the LED's output.
 */
export const CAMERA_WB = [1, 1.15, 1.6] as const;
export function lightColor(hex: string, k: number): [number, number, number] {
  const c = linearColor(hex).map((v, i) => v * CAMERA_WB[i]);
  const m = Math.max(c[0], c[1], c[2], 1e-6);
  return [(c[0] / m) * k, (c[1] / m) * k, (c[2] / m) * k];
}

/**
 * Light *thrown* by an LED of this color. A single-channel red or blue diode puts out a
 * fraction of a white LED's flux, so saturated washes are dimmer (perceptually ~√luminance)
 * while the LED itself still blows out to a hot core.
 */
export function thrownColor(hex: string, k: number): [number, number, number] {
  const c = lightColor(hex, 1);
  const luma = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  const sat = 1 - Math.min(c[0], c[1], c[2]) / Math.max(c[0], c[1], c[2], 1e-6);
  const f = Math.max(0.18, Math.min(1, Math.sqrt(luma / 0.75) * (1 - 0.4 * sat)));
  return [c[0] * k * f, c[1] * k * f, c[2] * k * f];
}
